import { getAppEnv } from "./utils";

const RECENT_STORE_PAYMENT_STORAGE_KEY = "recent-store-payment";
const MAX_RECENT_STORE_PAYMENTS = 10;
const RECENT_STORE_PAYMENT_DEDUP_MS = 5 * 1000;

export type RecentStorePayment = {
  storeId: string;
  paidAt: string;
};

function isRecentStorePayment(value: unknown): value is RecentStorePayment {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as RecentStorePayment).storeId === "string" &&
    typeof (value as RecentStorePayment).paidAt === "string"
  );
}

export function getRecentStorePaymentThresholdMs() {
  return getAppEnv() === "prod/live" ? 3 * 60 * 60 * 1000 : 3 * 60 * 1000;
}

export function shouldConfirmRecentStorePayment(params: {
  currentStoreId: string;
  currentTime: Date;
  recentStorePayments: RecentStorePayment[] | null;
}) {
  const { currentStoreId, currentTime, recentStorePayments } = params;

  if (!recentStorePayments || recentStorePayments.length === 0) {
    return false;
  }

  return recentStorePayments.some((recentStorePayment) => {
    if (recentStorePayment.storeId !== currentStoreId) {
      return false;
    }

    const paidAt = new Date(recentStorePayment.paidAt);

    if (Number.isNaN(paidAt.getTime())) {
      return false;
    }

    return (
      currentTime.getTime() - paidAt.getTime() <
      getRecentStorePaymentThresholdMs()
    );
  });
}

export function setRecentStorePayment(data: RecentStorePayment) {
  const recentStorePayments = getRecentStorePayment() ?? [];
  const currentPaidAt = new Date(data.paidAt);
  const nextRecentStorePayments = [
    data,
    ...recentStorePayments.filter((recentStorePayment) => {
      if (recentStorePayment.storeId !== data.storeId) {
        return true;
      }

      const paidAt = new Date(recentStorePayment.paidAt);

      if (
        Number.isNaN(currentPaidAt.getTime()) ||
        Number.isNaN(paidAt.getTime())
      ) {
        return true;
      }

      return (
        Math.abs(currentPaidAt.getTime() - paidAt.getTime()) >
        RECENT_STORE_PAYMENT_DEDUP_MS
      );
    }),
  ].slice(0, MAX_RECENT_STORE_PAYMENTS);

  localStorage.setItem(
    RECENT_STORE_PAYMENT_STORAGE_KEY,
    JSON.stringify(nextRecentStorePayments),
  );
}

export function getRecentStorePayment() {
  const raw = localStorage.getItem(RECENT_STORE_PAYMENT_STORAGE_KEY);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);

    if (Array.isArray(parsed) && parsed.every(isRecentStorePayment)) {
      return parsed as RecentStorePayment[];
    }
  } catch {
    /* ignore parse errors */
  }

  localStorage.removeItem(RECENT_STORE_PAYMENT_STORAGE_KEY);
  return null;
}

export function clearRecentStorePayment() {
  localStorage.removeItem(RECENT_STORE_PAYMENT_STORAGE_KEY);
}
